import { Box, Divider, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import { JSX, ReactNode } from 'react';

interface InformationBlockProps {
  /**
   * Title of the block
   */
  title: ReactNode;
  /**
   * Subtitle of the block, displayed next to the title
   */
  subTitle?: string;
  /**
   * Description of the block
   */
  description: string;
  /**
   * Action button (or any other component)
   */
  action?: ReactNode;
  /**
   * If provided, the block will be displayed as active/inactive (with active icon and "muted" subtitle)
   */
  active?: boolean;
}

/**
 * Bordered information block with title, description and optional action button
 */
const InformationBlock = ({
  title,
  description,
  action,
  active,
  subTitle,
}: InformationBlockProps): JSX.Element => (
  <Box border={1} borderColor="common.borderGray" className="p-4 flex justify-between items-center">
    <Box className="flex">
      {active !== undefined && (
        <Box className="mt-1 mr-3">
          {active ? (
            <Box
              component={Icon}
              width={20}
              height={20}
              icon="material-symbols:check-circle"
              color="success.main"
            />
          ) : (
            <Box width={20} height={20} />
          )}
        </Box>
      )}
      <Box className="max-w-lg">
        <Box className="flex space-x-3 h-8 whitespace-nowrap">
          <Typography variant="subtitle1" fontWeight={800}>
            {title}
          </Typography>
          {subTitle && (
            <>
              <Divider orientation="vertical" />
              <Typography
                variant="subtitle1"
                color={active || active === undefined ? 'text.primary' : 'text.disabled'}
              >
                {subTitle}
              </Typography>
            </>
          )}
        </Box>
        <Typography color="text.secondary">{description}</Typography>
      </Box>
    </Box>
    <Box>{action}</Box>
  </Box>
);

export default InformationBlock;
